import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {exportCSVFile, formatMoney} from "../../../../../../core/services/utils/utils";
import Button from "../../../../../components/base/button/button";
import {Paper, SvgIcon, useTheme} from "@mui/material";
import {Col, Row} from "reactstrap";
import BarChart from "../../../../../components/base/chart/bar-chart";
import ToggleButtons from "../../../../../components/base/button/toggle_buttons";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import FreightFeesReportSearchSection from "../../../../../components/search-sections/freight-fees-reports";
import {ReactComponent as Excel} from "../../../../../../assets/images/excel.svg";
import Api from "../../../../../../core/services/api/api";
import ToastService from "../../../../../../core/services/toast";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import useAccessLevels from "../../../../../hooks/use-access-levels";


const ShippingFeesReportTabs = {
    quantity: 'quantity',
    cost: 'cost'
}

const ShippingFeesReportsView = () => {
    const [creatingReport, setCreatingReport] = useState(false);
    const [records, setRecords] = useState([]);
    const isMounted = useIsMounted();
    const counterRef = useRef(1);
    const {configureNotebookTags} = useConfigureNotebook('shipping-fees-reports-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();
    const canExportShippingFeeReports = operatorAccessLevels.packages.sales.exportShippingFeeReports;
    const [selectedTab, setSelectedTab] = useState(ShippingFeesReportTabs.quantity);

    const tabs = useMemo(() => Object.values(ShippingFeesReportTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case ShippingFeesReportTabs.quantity:
                    return 'Quantity';
                case ShippingFeesReportTabs.cost:
                    return 'Cost';
                default:
                    return "";
            }
        })(),
    })), [])

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool
     */
    useEffect(() => {
        configureNotebookTags((tags) => tags.shippingFeesReports);
        configureBehavioralTool((tags) => tags.shippingFeesReports);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of the indicator for checking if the view is loading or not
     * @type {unknown}
     */
    const loading = useMemo(() => creatingReport, [creatingReport])

    /**
     * Adds new data to the [records] in the state
     * @param report
     * @return {Promise<void | Record<string, string>>}
     */
    const handleAddingReport = useCallback(async (report) => {
        setCreatingReport(true);
        if (records.map(e => e.reportName).includes(report.reportName)) {
            setCreatingReport(false);
            return {
                'reportName': 'Report name cannot be duplicate',
            }
        }
        const forApi = {
            regionIds: report.regions?.map(e => e.id) ?? null,
            customerIds: report.customers?.length ? report.customers : null,
            freightCompanyIds: report.freightCompanies?.map(e => e.id) ?? null,
            startDate: report?.startDate,
            endDate: report?.endDate,
        }
        const response = await Api.getFreightOrdersReport(forApi);
        if (!isMounted())
            return;
        setCreatingReport(false);
        if (response?.resultFlag) {
            if (!response.data.length) {
                ToastService.warning({title: "No Data Found", message: 'No data found for the selected filters to create a report from.'});
                return {};
            }
            setRecords(prevState => [
                ...prevState,
                {
                    id: counterRef.current++,
                    reportName: report.reportName,
                    records: response.data.map(record => ({
                        id: record.id,
                        submittedDate: record.submittedDateTime,
                        estimatedFee: record.estimatedFee,
                        customerFee: record.customerFee,
                        freightFee: record.actualFee,
                        customer: {
                            name: record.customerName,
                            id: record.customerId,
                        },
                        region: {
                            title: record.regionTitle,
                            id: record.regionId,
                        },
                        freightCompany: {
                            title: record.freightCompanyTitle,
                            id: record.freightCompanyId,
                        },
                        exportEntity: record,
                    }))
                }
            ])
        }
        return (response?.resultFlag ?? false) ? (void 0) : {};
    }, [isMounted, records])

    /**
     * Clears the added reports by setting [records] in the inner state to an empty array
     */
    const clearRecords = useCallback(() => setRecords([]), [])

    /**
     * Exports the added reports in the state to csv file
     */
    const exportToCSV = useCallback(() => {
        const exportTitle = `Freight Fees Report`;
        const toBeExported = records?.map(e => e.records).flat()?.map((record, index) => {
            return {
                '': index + 1,
                "Id": record.exportEntity.id,
                "Freight Quote": record.estimatedFee,
                "Customer Charges": record.customerFee ?? '',
                "Freight Fee": record.freightFee ?? '',
                "Customer": record.customer?.name ?? '',
                "Region": record.region?.title ?? '',
                "Freight Company": record.freightCompany?.title ?? '',
                "Submitted Date Time": record.exportEntity.submittedDateTime ? DateUtils.serverToLocal(record.exportEntity.submittedDateTime).format(DateUtils.dateFormat) : '--',
            }
        })
        if (!toBeExported.length) {
            ToastService.warning({message: "No records found for exporting!"});
            return;
        }
        exportCSVFile(toBeExported, exportTitle);
    }, [records])

    return (
        <div className={'shipping-fees-report-view'}>
            <div className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                <p className={'view-title'}>
                    Shipping Fees Reports
                </p>
            </div>
            <FreightFeesReportSearchSection
                loading={loading}
                handleAddingReport={handleAddingReport}
            />
            <div className={'mb-3 w-100'}/>
            {
                ((records?.length ?? 0) > 0) &&
                <>
                    <div className={'d-flex w-100 justify-content-between'}>
                        <div>
                            <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                                {
                                    tabs?.map((tab) => (
                                        <Tab key={tab.value} value={tab.value} label={tab.name}/>
                                    ))
                                }
                            </Tabs>
                        </div>
                        <div className={'d-flex'}>
                            <Button
                                className={'ml-3 d-flex justify-content-center align-items-center'}
                                variant={'outlined'}
                                color={'primary'}
                                onClick={clearRecords}
                            >
                                Clear
                            </Button>
                            {
                                canExportShippingFeeReports &&
                                <Button
                                    className={'ml-3 d-flex justify-content-center align-items-center'}
                                    variant={'outlined'}
                                    color={'primary'}
                                    onClick={exportToCSV}
                                >
                                    <SvgIcon className='icon normal' component={Excel}/>
                                    <p className='ml-2'>Export to Excel</p>
                                </Button>
                            }
                        </div>
                    </div>
                    <Paper className={'paper mx-2 mt-4 px-3 d-flex justify-content-center'}>
                        <ChartSection
                            dataset={records}
                            loading={loading}
                            isQuantity={selectedTab === ShippingFeesReportTabs.quantity}
                        />
                    </Paper>
                </>
            }
        </div>
    );
}

/**
 * Types of available visibilities for the chart
 */
const chartVisibilityTypes = {
    regions: 'regions',
    topCustomers: 'topCustomers',
    freightCompanies: 'freightCompanies',
    yearly: 'yearly',
    monthly: 'monthly',
    dayOfTheWeek: 'dayOfTheWeek',
}

const ChartSection = ({dataset, loading, isQuantity}) => {
    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])
    const [visibility, setVisibility] = useState(chartVisibilityTypes.dayOfTheWeek);

    const visibilities = useMemo(() => [
        {
            value: chartVisibilityTypes.regions,
            name: "Regions",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.topCustomers,
            name: "Top Customers",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.freightCompanies,
            name: "Freight Companies",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.yearly,
            name: "Yearly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.monthly,
            name: "Monthly",
            disabled: loading
        },
        {
            value: chartVisibilityTypes.dayOfTheWeek,
            name: "Daily",
            disabled: loading
        },
    ], [loading])

    const rawDataSet = useRef({});

    /**
     * Gets the string representation of the day of the week of date
     * @param {DateUtilsTypes.Instance} date
     * @return {string}
     */
    const dayOfWeek = useCallback((date) => {
        const dow = date.day();
        switch (dow) {
            case 0:
            default:
                return "Sunday";
            case 1:
                return "Monday";
            case 2:
                return "Tuesday";
            case 3:
                return "Wednesday";
            case 4:
                return "Thursday";
            case 5:
                return "Friday";
            case 6:
                return "Saturday";
        }
    }, [])

    /**
     * Converts day string to number
     * @param day
     * @return {number}
     */
    const dayToNumber = useCallback((day) => {
        switch (day) {
            case "Sunday":
            default:
                return 0;
            case "Monday":
                return 1;
            case "Tuesday":
                return 2;
            case "Wednesday":
                return 3;
            case "Thursday":
                return 4;
            case "Friday":
                return 5;
            case "Saturday":
                return 6;
        }
    }, [])

    const getDatasetData = useCallback((data = []) => {
        let aggregated = {};

        let keyFetcher;
        switch (visibility) {
            case chartVisibilityTypes.regions:
                keyFetcher = (record) => record.region.title;
                break;
            case chartVisibilityTypes.topCustomers:
                keyFetcher = (record) => record.customer.name;
                break;
            case chartVisibilityTypes.freightCompanies:
                keyFetcher = (record) => record.freightCompany?.title;
                break;
            case chartVisibilityTypes.yearly:
                keyFetcher = (record) => DateUtils.serverToLocal(record.submittedDate).format('YYYY');
                break;
            case chartVisibilityTypes.monthly:
                keyFetcher = (record) => DateUtils.serverToLocal(record.submittedDate).format('MMM YYYY');
                break;
            case chartVisibilityTypes.dayOfTheWeek:
                keyFetcher = (record) => dayOfWeek(DateUtils.serverToLocal(record.submittedDate));
                break;
            default:
                break;
        }

        for (const record of data) {
            const key = keyFetcher(record);
            if (!aggregated[key])
                aggregated[key] = {
                    estimatedFee: record.estimatedFee,
                    customerFee: record.customerFee,
                    actualFee: record.freightFee,
                    count: 1,
                };
            else {
                aggregated[key].estimatedFee += record.estimatedFee;
                aggregated[key].customerFee += record.customerFee;
                aggregated[key].actualFee += record.freightFee;
                aggregated[key].count += 1;
            }
        }

        return aggregated;
    }, [dayOfWeek, visibility])


    /**
     * Creates the tool tip value based on the percentage range of the bar
     * @type {(function(*): string)}
     */
    const labelValue = useCallback((tooltipItem) => {
        return tooltipItem.dataset.label
    }, [])

    /**
     * renders the tool tip value of the bar entity.
     * @type {Function}
     */
    const afterLabelValue = useCallback((tooltipItem) => {
        // we return the quantity
        return isQuantity ? `Quantity: ${tooltipItem.dataset.data[tooltipItem.label]}` : '';
    }, [isQuantity]);

    /**
     * Renders the estimated fee value of the char's bar entity.
     * @type {(function([*]): (string|string))}
     */
    const estimatedFeeValue = useCallback(([tooltipItem]) => {
        const key = tooltipItem.dataset.label.replace(" Freight Quotes", "").replace(" Customer Charges", "").replace(" Actual Fees", "");
        const rawData = rawDataSet.current[key];
        if (!rawData)
            return "";
        const data = rawData[tooltipItem.label];
        return `Freight Quote: ${formatMoney(data.estimatedFee)}`
    }, [])

    /**
     * Renders the customer fee value of the char's bar entity.
     * @type {(function([*]): (string|string))}
     */
    const customerFeeValue = useCallback(([tooltipItem]) => {
        const key = tooltipItem.dataset.label.replace(" Freight Quotes", "").replace(" Customer Charges", "").replace(" Actual Fees", "");
        const rawData = rawDataSet.current[key];
        if (!rawData)
            return "";
        const data = rawData[tooltipItem.label];
        return `Customer Charges: ${formatMoney(data.customerFee)}`
    }, [])

    /**
     * Renders the actual fee value of the char's bar entity.
     * @type {(function([*]): (string|string))}
     */
    const actualFeeValue = useCallback(([tooltipItem]) => {
        const key = tooltipItem.dataset.label.replace(" Freight Quotes", "").replace(" Customer Charges", "").replace(" Actual Fees", "");
        const rawData = rawDataSet.current[key];
        if (!rawData)
            return "";
        const data = rawData[tooltipItem.label];
        return `Actual Fee: ${formatMoney(data.actualFee)}`
    }, [])

    /**
     * Memo version of chart data passed to the bar-chart
     * @type {{datasets: *[], labels: *[]}}
     */
    const chartData = useMemo(() => {
        const chartData = {
            datasets: [],
            labels: [],
        }
        let i = 0;
        let j = chartColors.length - 3;
        for (const e of dataset) {
            const index = dataset.indexOf(e);
            const allData = getDatasetData(e.records);
            rawDataSet.current[e.reportName] = allData;

            if (isQuantity) {
                chartData.datasets.push({
                    data: Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.count])),
                    label: e.reportName,
                    backgroundColor: chartColors[index % chartColors.length],
                });
            } else {
                let colorI = index % 2 === 0 ? i : j;
                chartData.datasets.push(...[
                    {
                        data: Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.estimatedFee])),
                        label: `${e.reportName} Freight Quotes`,
                        backgroundColor: chartColors[(colorI++) % chartColors.length],
                    },
                    {
                        data: Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.customerFee])),
                        label: `${e.reportName} Customer Charges`,
                        backgroundColor: chartColors[(colorI++) % chartColors.length],
                    },
                    {
                        data: Object.fromEntries(Object.entries(allData).map(([key, value]) => [key, value.actualFee])),
                        label: `${e.reportName} Actual Fees`,
                        backgroundColor: chartColors[(colorI++) % chartColors.length],
                    },
                ]);
            }

            const _labels = [...new Set([...chartData.labels, ...Object.keys(allData)])];
            switch (visibility) {
                case chartVisibilityTypes.dayOfTheWeek:
                    _labels.sort((a, b) => dayToNumber(a) - dayToNumber(b));
                    break;
                case chartVisibilityTypes.yearly:
                    _labels.sort((a, b) => Number(a) - Number(b));
                    break;
                case chartVisibilityTypes.monthly:
                    _labels.sort((a, b) => DateUtils.serverToLocal(a).isBefore(DateUtils.serverToLocal(b), 'month') ? -1 : DateUtils.serverToLocal(a).isAfter(DateUtils.serverToLocal(b), 'month') ? 1 : 0)
                    break;
                case chartVisibilityTypes.regions:
                case chartVisibilityTypes.topCustomers:
                case chartVisibilityTypes.freightCompanies:
                    _labels.sort((a, b) => {
                        const aData = allData[a];
                        const bData = allData[b];
                        if (!aData || !bData)
                            return 0;
                        if (isQuantity) {
                            return bData.count - aData.count;
                        } else {
                            return bData.estimatedFee - aData.estimatedFee;
                        }
                    })
                    break;
                default:
                    break;
            }
            chartData.labels = _labels;
        }
        if (visibility === chartVisibilityTypes.topCustomers) {
            chartData.labels = chartData.labels.slice(0, 10);
            for (const dataset of chartData.datasets) {
                dataset.data = Object.fromEntries(Object.entries(dataset.data).filter(([key]) => chartData.labels.includes(key)));
            }
        }
        return chartData;
    }, [chartColors, dataset, dayToNumber, getDatasetData, isQuantity, visibility]);

    return (
        <Row className={'d-flex w-100 mb-4'}>
            <Col xs={12}
                 className={'my-4 transition mx-auto bar-chart'}>
                <BarChart
                    height={window.innerHeight / 2}
                    data={chartData}
                    tooltipCallbacks={{
                        footer: estimatedFeeValue,
                        afterFooter: customerFeeValue,
                        beforeFooter: actualFeeValue,
                        label: labelValue,
                        afterLabel: afterLabelValue,
                    }}
                    displayLegend={true}
                    scales={{
                        scales: {
                            x: {},
                            y: {
                                // we want ticks to be integers
                                ticks: {
                                    ...(isQuantity && {
                                        maxTicksLimit: Math.ceil(Math.max(...chartData.datasets.map(e => Math.max(...Object.values(e.data))))),
                                    }),
                                    callback: (value) => !isQuantity ? formatMoney(value) : parseInt(value),
                                }
                            },
                        }
                    }}
                />
            </Col>
            <Col xs={12} className={'d-flex justify-content-center mb-4'}>
                <ToggleButtons
                    size={'medium'}
                    value={visibility}
                    onChange={(_, v) => v && setVisibility(v)}
                    options={visibilities}
                />
            </Col>
        </Row>
    );

}

export default ShippingFeesReportsView;
